import { Component } from "react";
import moment from "moment";
import { toast } from "extensions/toast";
import showdown from "showdown";
import DOMPurify from "dompurify";

import { hashStringToColor } from "common/utils";

import { request } from "common/components/request";
import { ErrorView } from "common/components/error";
import { Webix } from "./webix";

// Prevent Showdown to wrap comments into <p>
// https://github.com/showdownjs/showdown/issues/588
showdown.extension("customOuterWrapper", () => [{
    type: "listener",
    listeners: {
        "hashHTMLBlocks.after": function(event, text, converter, options, globals) {
            return text.replace(/^ {0,3}<[a-z]+\b[^>]*>$/gim, (wm) => `\n\n¨K${globals.gHtmlBlocks.push(wm) - 1}K\n\n`);
        }
    }
}]);

// TODO: Move converter into common spot for both comments and redux form editor widget
const converter = new showdown.Converter({
    extensions: ["customOuterWrapper"],
    tables: true,
    tasklists: true,
    strikethrough: true,
    parseImgDimensions: true,
    emoji: true,
    simpleLineBreaks: true
});

export class WebixComments extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            has_error: false,
            comments: []
        };
    }
    
    componentDidMount() {
        this.fetchPromise = this.fetchComments();
    }
    
    componentWillUnmount() {
        if (this.fetchPromise) {
            this.fetchPromise.cancel();
        }
    }
    
    postComment(id, operation, data) {
        return request(this.props.url, {
            method: "POST",
            data: {
                id,
                operation,
                data
            }
        }).then((response) => {
            if (operation === "insert") {
                this.setState((prevState) => ({
                    comments: [
                        ...prevState.comments,
                        {
                            id: response.id, // override webix comment id with our from the API
                            ...data
                        }
                    ]
                }));
            }
            
            return response;
        });
    }
    
    fetchComments() {
        if (!this.props.url) {
            throw new Error("Missing comments url endpoint");
        }
        
        return request(this.props.url).then((data) => {
            this.setState({
                comments: data.map((comment) => ({
                    ...comment,
                    date: moment.utc(comment.date).local().format("YYYY-MM-DD HH:mm:ss")
                })),
                loading: false
            });
        }, (error) => {
            this.setState({
                loading: false,
                has_error: true
            });
        });
    }
    
    render() {
        if (this.state.loading) {
            return (
                <div className={this.props.className}>
                    <div className="text-input__loading--line medium" />
                    <div className="text-input__loading--line long" />
                </div>
            );
        }
        
        if (this.state.has_error) {
            return <ErrorView message="Unable to fetch comments" header="Error" custom />;
        }
        
        const ui = {
            ...this.props.ui,
            mentions: {
                textValue: "handle"
            },
            listItem: {
                templateAvatar: (obj, common) => {
                    const user = this.props.users.find(({ id }) => id === obj.user_id) || { username: "bot", initials: "PZ" };
                    return `<div class="webix_comments_avatar profile-photo" style="background-color:${hashStringToColor(user.username)}">${user.initials}</div>`;
                },
                templateText: (obj, common) => {
                    // Sanitize
                    const clean = DOMPurify.sanitize(obj.text);
                    
                    // Convert MD to HTML and set back to object
                    // Mutation is evil. Create a new obj instead
                    const newObject = {
                        ...obj,
                        text: converter.makeHtml(clean)
                    };
                    
                    return `<div class="webix_comments_message">${common.templateMentioned(newObject, common)}</div>`;
                },
                templateDate: ({ date }) => `<span class="webix_comments_date">${moment(date).format("MM-DD-YYYY h:mm a")}</span>`
            },
            currentUser: this.props.currentUser,
            users: [
                ...this.props.users,
                {
                    id: "5d168830e8df233b4c3288a4",
                    username: "Bot",
                    initials: "BT",
                    handle: "bot",
                    value: "Bot"
                }
            ],
            save: (function(post) {
                return function(id, operation, data) {
                    if (this._dontCallServerRemove) {
                        delete this._dontCallServerRemove;
                        return;
                    }
                    
                    post(id, operation, data).then((response) => {
                        const verbs = {
                            insert: "inserted",
                            "delete": "deleted",
                            update: "updated"
                        };
                        
                        toast.success(`Successfully ${verbs[operation]} a comment.`);
                    }, (error) => {
                        toast.error(`Unable to ${operation} a comment. Check browser console.`);
                        if (operation === "insert") {
                            this._dontCallServerRemove = true;
                            this.remove(id);
                        }
                    });
                };
            }(this.postComment.bind(this))),
            view: "comments"
        };
        
        return <Webix className={this.props.className} ui={ui} data={this.state.comments}/>;
    }
}
